import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

/**
 * Localizes navigation item labels using react-intl.
 *
 * All message descriptors live inside `intl.formatMessage()` calls so the
 * formatjs extractor can discover them and they execute within React intl context.
 *
 * Items whose id matches a known entry are translated; others
 * (e.g. brand names like "LuxPlus+" or "Société") pass through unchanged.
 */
function useLocalizedNavItems(navItems: Array<App.NavItem>): Array<App.NavItem> {
  const intl = useIntl()

  return useMemo(() => {
    const localizedTextById: Record<string, string> = {
      'nav-link-hotels': intl.formatMessage({
        id: 'SXbAI/CoXe',
        defaultMessage: 'Hotels',
        description: 'Navigation link label for hotels on the hotel or room booking page',
      }),
      'hotelsandresorts': intl.formatMessage({
        id: 'mv0NUuCN+J',
        defaultMessage: 'Hotels & Resorts',
        description: 'Navigation link label for hotels and resorts (white label brands)',
      }),
      'nav-link-homes-and-villas': intl.formatMessage({
        id: '8LeYyKFJg8',
        defaultMessage: 'Homes & Villas',
        description: 'Navigation link label for homes and villas on the rental page',
      }),
      'nav-link-tours': intl.formatMessage({
        id: 'y5zDVp0bcP',
        defaultMessage: 'Tours',
        description: 'Navigation link label for tours on the tour offer detail page',
      }),
      'nav-link-tours-cruises': intl.formatMessage({
        id: 'xQps7CG9PO',
        defaultMessage: 'Tours & Cruises',
        description: 'Navigation link label for tours and cruises in the cruise section',
      }),
      'nav-link-cruises': intl.formatMessage({
        id: 'zZtVcxihXk',
        defaultMessage: 'Cruises',
        description: 'Navigation link label for cruises in the cruise offer or booking section',
      }),
      'nav-link-flights': intl.formatMessage({
        id: 'krMvDAr0uY',
        defaultMessage: 'Flights',
        description: 'Navigation link label for flights in the flight search or booking flow',
      }),
      'nav-link-car-hire': intl.formatMessage({
        id: 'eN5d8Ihn9E',
        defaultMessage: 'Car Hire',
        description: 'Navigation link label for car hire in the car hire booking section',
      }),
      'nav-link-ultralux': intl.formatMessage({
        id: 'Vixt28o298',
        defaultMessage: 'Ultra Lux',
        description: 'Navigation link label for ultra lux for computed display labels',
      }),
      'nav-link-experiences': intl.formatMessage({
        id: 'xniFVpzkLv',
        defaultMessage: 'Experiences',
        description: 'Navigation link label for experiences on the experience offer or booking page',
      }),
      'nav-link-maldives-packages': intl.formatMessage({
        id: 'TVTE2GfMvV',
        defaultMessage: 'Maldives Bundles',
        description: 'Navigation link label for Maldives bundles for computed display labels',
      }),
      'nav-link-inspiration': intl.formatMessage({
        id: 'L5tPNRc9zu',
        defaultMessage: 'Inspiration',
        description: 'Navigation link label for inspiration for computed display labels',
      }),
      'nav-link-support': intl.formatMessage({
        id: 'B+qwt8A++q',
        defaultMessage: 'Support',
        description: 'Navigation link label for support in the customer support section',
      }),
      'nav-link-store': intl.formatMessage({
        id: 'FBFpURkKBN',
        defaultMessage: 'Visit Us In Store',
        description: 'Navigation link label for store visit for computed display labels',
      }),
      'nav-link-gift-cards': intl.formatMessage({
        id: 'dn4C1HGzna',
        defaultMessage: 'Gift Cards',
        description: 'Navigation link label for gift cards on the gift card purchase or redemption page',
      }),
      'nav-link-insurance': intl.formatMessage({
        id: '0uCIrvaDC7',
        defaultMessage: 'Travel Protection',
        description: 'Navigation link label for travel protection in the insurance flow',
      }),
      'nav-link-marketplace': intl.formatMessage({
        id: 'yj4Tt2PRML',
        defaultMessage: 'Shop',
        description: 'Navigation link label for shop/marketplace for computed display labels',
      }),
      'nav-link-refer': intl.formatMessage({
        id: 'di1TiTwCs8',
        defaultMessage: 'Refer a Friend',
        description: 'Navigation link label for refer a friend for computed display labels',
      }),
      'nav-link-le4b': intl.formatMessage({
        id: 'r0nhk6dhtQ',
        defaultMessage: 'Business Travel',
        description: 'Navigation link label for business travel for computed display labels',
      }),
      'nav-link-golf': intl.formatMessage({
        id: 'I9Wq2BBPti',
        defaultMessage: 'Golf',
        description: 'Navigation link label for golf for computed display labels',
      }),
      'nav-link-groups': intl.formatMessage({
        id: 'RH3MYjnXMj',
        defaultMessage: 'Groups',
        description: 'Navigation link label for groups for computed display labels',
      }),
      'nav-link-weddings': intl.formatMessage({
        id: 'kzC5Y7pk5h',
        defaultMessage: 'Weddings',
        description: 'Navigation link label for weddings for computed display labels',
      }),
      'nav-link-more': intl.formatMessage({
        id: 'fzR/C3wS9A',
        defaultMessage: 'More',
        description: 'Navigation link label for the more dropdown for computed display labels',
      }),
      'nav-link-best-of-2025': intl.formatMessage({
        id: 'xqWUIcRCNd',
        defaultMessage: 'Best of 2025',
        description: 'Navigation link label for best of 2025 campaign for computed display labels',
      }),
      'nav-link-best-of-2025-eu': intl.formatMessage({
        id: 'xqWUIcRCNd',
        defaultMessage: 'Best of 2025',
        description: 'Navigation link label for best of 2025 campaign for computed display labels',
      }),
      'nav-link-best-of-2025-us': intl.formatMessage({
        id: 'xqWUIcRCNd',
        defaultMessage: 'Best of 2025',
        description: 'Navigation link label for best of 2025 campaign for computed display labels',
      }),
    }

    if (isAgentHubEnabled) {
      localizedTextById['nav-link-flights'] = intl.formatMessage({
        id: 'vRx2ckV+2z',
        defaultMessage: 'Direct Maldives Flights',
        description: 'Navigation link label for direct Maldives flights for computed display labels',
      })
    }

    const localize = (item: App.NavItem): App.NavItem => {
      let text: string | undefined
      if (item.id) {
        // Both "Tours" and "Tours & Cruises" share id "nav-link-tours" in
        // navItemsByRegion. Disambiguate using the source text so each gets
        // its own translation.
        if (item.id === 'nav-link-tours' && item.text === 'Tours & Cruises') {
          text = localizedTextById['nav-link-tours-cruises']
        } else {
          text = localizedTextById[item.id]
        }
      }
      if (!text && !item.items?.length) return item

      return {
        ...item,
        text: text ?? item.text,
        items: item.items?.map(localize),
      }
    }

    return navItems.map(localize)
  }, [navItems, intl])
}

export default useLocalizedNavItems
